import { formatJobMessage, formatJobDuration } from './amazon-finance-job-message';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, catchError, of, switchMap, takeUntil, timer } from 'rxjs';
import {
  FinanceJobOverview,
  FinanceJobRun,
  FinanceJobStage,
  FinanceJobStatus
} from '../../services/amazon-finance.models';
import { AmazonFinanceService } from '../../services/amazon-finance.service';

@Component({
  selector: 'gung-amazon-finance-jobs',
  templateUrl: './amazon-finance-jobs.component.html',
  styleUrl: './amazon-finance-jobs.component.scss',
  standalone: false
})
export class AmazonFinanceJobsComponent implements OnInit, OnDestroy {
  @Input() compact = false;
  @Output() readonly openDetails = new EventEmitter<void>();

  readonly formatMessage = formatJobMessage;
  readonly formatDuration = formatJobDuration;
  readonly messageStatuses: Record<string, string> = {
    MAPPED: 'JOB_MESSAGE_MAPPED',
    MAPPED_MISSING_CUSTOMER_ID: 'JOB_MESSAGE_CUSTOMER',
    MAPPED_MISSING_DELIVERY_COUNTRY: 'JOB_MESSAGE_COUNTRY',
    MAPPED_MISSING_ORDER_ID: 'JOB_MESSAGE_ORDER',
    MAPPED_MISSING_MAPPINGS: 'JOB_MESSAGE_MAPPINGS',
    MAPPED_ORDER_LOOKUP_FAILED: 'JOB_MESSAGE_LOOKUP',
    MAPPED_ZERO_AMOUNT: 'JOB_MESSAGE_ZERO',
    MAPPED_NO_LINES: 'JOB_MESSAGE_NO_LINES',
    EXCEPTION: 'JOB_MESSAGE_EXCEPTION'
  };

  overview: FinanceJobOverview | null = null;
  loading = true;
  actionStage: FinanceJobStage | null = null;
  error = '';
  notice = '';

  private readonly refresh$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly financeService: AmazonFinanceService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    timer(0, 5000)
      .pipe(
        switchMap(() => this.load()),
        takeUntil(this.destroy$)
      )
      .subscribe();
    this.refresh$
      .pipe(
        switchMap(() => this.load()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.loading = true;
    this.refresh$.next();
  }

  run(stage: FinanceJobStage): void {
    if (!this.canRun()) {
      return;
    }
    if (
      (stage === 'POST' || stage === 'FULL_PIPELINE') &&
      !window.confirm(this.translate.instant(`AMAZON_FINANCE_JOB_CONFIRM_${stage}`))
    ) {
      return;
    }

    this.actionStage = stage;
    this.error = '';
    this.notice = '';
    this.financeService
      .runJob(stage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.actionStage = null;
          this.notice = result.message;
          this.refresh();
        },
        error: error => {
          this.actionStage = null;
          this.error = error?.error?.detail || error?.error?.message || 'AMAZON_FINANCE_JOB_RUN_ERROR';
        }
      });
  }

  canRun(): boolean {
    return (
      !!this.overview && !this.overview.running && !this.actionStage && this.overview.jobs.some(job => job.registered)
    );
  }

  stageJobs(): FinanceJobStatus[] {
    return this.overview?.jobs.filter(job => job.stage !== 'FULL_PIPELINE') ?? [];
  }

  latestRuns(): { stage: FinanceJobStage; run: FinanceJobRun }[] {
    return (this.overview?.jobs ?? [])
      .flatMap(job => job.recentRuns.map(run => ({ stage: job.stage, run })))
      .sort((left, right) => (right.run.startTime ?? '').localeCompare(left.run.startTime ?? ''))
      .slice(0, 15);
  }

  runningMessage(): string | null {
    return this.overview?.jobs.find(job => job.running)?.latestRun?.message ?? null;
  }

  status(job: FinanceJobStatus): string {
    if (job.running) {
      return 'RUNNING';
    }
    return job.latestRun?.status ?? (job.registered ? 'NEVER_RUN' : 'NOT_REGISTERED');
  }

  statusClass(status: string | null | undefined): string {
    switch (status) {
      case 'RUNNING':
        return 'status--running';
      case 'FINISHED':
        return 'status--success';
      case 'EXCEPTION':
      case 'STUCK':
        return 'status--danger';
      case 'VETOED':
        return 'status--warning';
      default:
        return 'status--neutral';
    }
  }

  private load() {
    return this.financeService.getJobs().pipe(
      switchMap(overview => {
        this.overview = overview;
        this.loading = false;
        this.error = '';
        return of(overview);
      }),
      catchError(() => {
        this.loading = false;
        this.error = 'AMAZON_FINANCE_JOBS_LOAD_ERROR';
        return of(null);
      })
    );
  }
}
